/**
 * Camera catalog — parsing + a pure mount→channel lookup over robot-config's own
 * camera list. No separate stored copy: `robotConfig.getCameras()` already holds the
 * live catalog, so this file only normalizes the wire payload and resolves a mount.
 */

export interface CameraCatalogEntry {
  mount: string;
  label: string;
  /**
   * Network-assigned channel index for this camera (wire field `uiSlot`/`ui_slot` in the
   * robot-config payload — the robot itself declares it, this module does not invent it).
   *
   * It is NOT a UI layout decision: the transport (`teleop-session`) uses this same
   * number as the array index for its video-consumer bookkeeping (`streams[slot]`, see
   * `media/video-pipeline.js`), and needs a stable index before any UI has rendered —
   * including a hardcoded legacy fallback for consumers that attach before robot-config
   * has even arrived (`LEGACY_MOUNT_SLOTS` in `signaling/consumer-slots.js`). The UI then
   * reuses this same channel number to look up the matching stream/slot when rendering.
   *
   * Renamed from the wire vocabulary `uiSlot` to `channel` here on purpose, so this
   * state module doesn't read as if it has an opinion about UI layout — it's just
   * carrying a network-assigned identifier through.
   */
  channel: number;
  enabled: boolean;
}

/**
 * Normalize cameras from robot-config payload into ordered catalog entries.
 */
export function normalizeCamerasPayload(cameras: unknown): CameraCatalogEntry[] {
  if (!Array.isArray(cameras)) return [];
  const out: CameraCatalogEntry[] = [];
  for (const item of cameras) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const mount = String(rec.mount || "")
      .trim()
      .toLowerCase();
    const label = String(rec.label || mount || "").trim();
    // Wire field is `uiSlot`/`ui_slot` (robot-config payload); mapped to `channel` below.
    const rawChannel = rec.uiSlot ?? rec.ui_slot;
    const channel = Number(rawChannel);
    const enabled = rec.enabled;
    if (!mount || !label || !Number.isFinite(channel) || channel < 0) continue;
    if (typeof enabled !== "boolean") continue;
    out.push({ mount, label, channel: Math.floor(channel), enabled });
  }
  out.sort((a, b) => a.channel - b.channel);
  return out;
}

/** Resolve a mount name to its channel index (see `CameraCatalogEntry.channel`). */
export function slotFromMount(cameras: CameraCatalogEntry[], mount: string): number | null {
  const key = String(mount || "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  const entry = cameras.find((c) => c.mount === key);
  return entry ? entry.channel : null;
}
